import React, {useCallback, useEffect, useRef} from 'react';
import FormContextProvider from 'contexts/form-context';
import {useDispatch} from 'react-redux';
import {useNewEmptyIllustration} from 'hooks/illustrator';
import {withProductComparisonList} from 'hooks/product-comparison';
import {widgetProductsQueryKey} from 'constants/query';
import {updateSelectedIllustration} from 'reducers/illustrator.reducer';
import {useCalculatorValidationSchema} from '../useCalculatorValidationSchema';
import CalculatorFormElement from '../calculator-form/CalculatorFormElement';
import Item from 'components/common/layout/item/Item';
import ProductsWidget from 'pages/widgets-page/calculator-page/components/products-widget/ProductsWidget';
import {EXCLUDE_ILLUSTRATOR_WARNING_MESSAGES} from 'constants/illustrator';
import CalculationWarnings from 'components/illustrator/calculation-warnings/CalculationWarnings';
import {pickUpdatingProductIllustrationContext, recordUpdatingWidgetCalculation} from 'lib/illustration-audit';
import {isEqual, pick} from 'lodash';

const WIDGET_BORROWER_INPUT_KEYS = ['DateOfBirth', 'ValueOfProperty', 'Liens', 'Zip'];

function pickWidgetInputs(data) {
  return {
    ...pick(data?.BorrowerProfile, WIDGET_BORROWER_INPUT_KEYS),
    OverrideClosingCosts: data?.OverrideClosingCosts,
    ClosingCost: data?.ClosingCost,
  };
}

CalculatorForm.propTypes = {};

function CalculatorForm(props) {
  const {data: {Calculations} = {}, loading, products} = props;

  const dispatch = useDispatch();
  const {defaultValues} = useNewEmptyIllustration({});
  const plainDefaultValues = JSON.parse(JSON.stringify(defaultValues));

  const comparisonData = props.data;

  // inputs submitted and waiting for their calculation, and the inputs behind the calculation on screen
  const pendingInputsRef = useRef(null);
  const calculatedInputsRef = useRef(null);

  const recordWidgetUpdate = useCallback(
    (widgetInputs, responseData) => {
      const responseCalculations = responseData.Calculations ?? [];
      const {calculationId} = pickUpdatingProductIllustrationContext(responseCalculations, products);

      recordUpdatingWidgetCalculation({
        borrowerProfileId: responseData.BorrowerProfileId,
        calculationId,
        widgetInputs,
        calculations: responseCalculations
          .filter((calculation) => calculation.CalculationId)
          .map((calculation) => ({
            productSelected: products.find((product) => product.ProductId === calculation.ProductId)?.ApiRoute ?? null,
            calculationId: calculation.CalculationId,
          })),
      });
    },
    [products],
  );

  // When the inputs change, the response that matches them arrives through the re-keyed comparison query rather than
  // through the refetch below, which still targets the previous (stale) query on the first submit.
  useEffect(() => {
    if (!comparisonData || !pendingInputsRef.current) return;

    const widgetInputs = pendingInputsRef.current;
    pendingInputsRef.current = null;
    calculatedInputsRef.current = widgetInputs;

    recordWidgetUpdate(widgetInputs, comparisonData);
  }, [comparisonData, recordWidgetUpdate]);

  const onSubmit = useCallback(
    async (data) => {
      const widgetInputs = pickWidgetInputs(data);
      const sameInputs = isEqual(widgetInputs, calculatedInputsRef.current);

      pendingInputsRef.current = sameInputs ? null : widgetInputs;

      dispatch(updateSelectedIllustration(data));
      const result = await props.refetch();

      // same inputs: the refetch hits the query that is already on screen, so its response is the right one
      if (sameInputs && result?.data) recordWidgetUpdate(widgetInputs, result.data);
    },
    [dispatch, props.refetch, recordWidgetUpdate],
  );

  const {validationSchema} = useCalculatorValidationSchema();

  return (
    <>
      <Item xs={12}>
        <FormContextProvider defaultState={{validationSchema}}>
          <CalculatorFormElement onSubmit={onSubmit} defaultValues={plainDefaultValues} loading={loading} />
        </FormContextProvider>
        <CalculationWarnings
                      products={products}
                      exclude={EXCLUDE_ILLUSTRATOR_WARNING_MESSAGES}
                      loading={loading}
                    />
      </Item>

      <Item xs={12}>
        <ProductsWidget Calculations={Calculations} />
      </Item>
    </>
  );
}

export default withProductComparisonList(CalculatorForm, {
  includeAllProducts: true,
  productsQueryKey: widgetProductsQueryKey,
});
